import React, { ReactNode, FC, useRef, useMemo } from "react";
import { motion, useScroll, useTransform, MotionValue } from "framer-motion";
import { interpolateColor } from "@tennr/frontend/utils/interpolateColor";
import { useBackgroundColor } from "@tennr/frontend/context";
import "./Heading.css";

export interface HeadingColorConfig {
  fromColor: string;
  toColor: string;
  useTransformArray?: [number, number, number, number];
}

export interface DynamicColorConfig {
  type: "dynamic";
  lightTextColor?: string;
  darkTextColor?: string;
  algorithm?: "invert" | "luminance";
  strength?: number; // 0-1, controls intensity of contrast effect
  debug?: boolean;
}

export type HeadingColorVariant = "dark" | "light" | "dynamic" | HeadingColorConfig | DynamicColorConfig;
export type HeadingPositionVariant = "left" | "center" | "right";
export type HeadingVariant = "xl" | "hero" | "section" | "tertiary";
export type HeadingVerticalAlignVariant = "top" | "center" | "bottom";
export type TextWrapValue = "wrap" | "nowrap" | "balance" | "stable" | "pretty";

export interface HeadingProps {
  // primary content
  heading: string;
  title?: string;
  body?: string | React.ReactNode;

  // sizing variant
  variant?: HeadingVariant;

  // positioning
  position?: HeadingPositionVariant;
  mobilePosition?: HeadingPositionVariant; // Override position for mobile (480px breakpoint)
  verticalAlign?: HeadingVerticalAlignVariant;
  textAlign?: "left" | "center" | "right"; // Text alignment independent of position
  mobileTextAlign?: "left" | "center" | "right"; // Text alignment for mobile

  // color variants
  color?: HeadingColorVariant;

  // dynamic color control
  forceDynamicColor?: boolean; // Defaults to true, set to false to disable dynamic color

  // legacy support
  dark?: boolean; // Deprecated
  light?: boolean; // Deprecated

  // optional React children
  children?: ReactNode;

  // layout
  width?: string | number; // Custom width override
  mobileMaxWidth?: string | number; // Mobile max-width override (768px breakpoint)
  wrapHeading?: TextWrapValue;

  // additional
  className?: string;
  multiLineTitle?: boolean;
}

export const Heading: FC<HeadingProps> = ({
  heading,
  title,
  body,
  variant,
  position = "left",
  mobilePosition,
  verticalAlign,
  textAlign,
  mobileTextAlign,
  color = "dynamic",
  forceDynamicColor = true,
  dark,
  light,
  children,
  width,
  mobileMaxWidth,
  className = "",
  wrapHeading = "wrap",
  multiLineTitle
}) => {
  const headingRef = useRef<HTMLDivElement>(null);
  const { getContrastingColor, scrollProgress, isTransitioning, transitionDuration } = useBackgroundColor();

  // Use local scroll only for non-dynamic color modes that need position-based transitions
  const { scrollYProgress } = useScroll({
    target: headingRef,
    offset: ["start end", "end start"],
  });

  // handle legacy props and forceDynamicColor
  const effectiveColor = forceDynamicColor ? "dynamic" : (dark ? "dark" : light ? "light" : color);

  // Check if using dynamic mode
  const isDynamic =
    effectiveColor === "dynamic" ||
    (typeof effectiveColor === "object" && "type" in effectiveColor && effectiveColor.type === "dynamic");

  // Memoize color configuration
  const { fromColor, toColor, transformArray } = useMemo(() => {
    let from: string;
    let to: string;
    let transform: [number, number, number, number] = [0, 0.3, 0.7, 1];

    if (typeof effectiveColor === "string") {
      if (effectiveColor === "dynamic") {
        // For dynamic mode, we'll use the context color directly
        from = "";
        to = "";
      } else if (effectiveColor === "dark") {
        from = "#5F4037";
        to = "#5F4037";
      } else {
        from = "#EEDFD1";
        to = "#EEDFD1";
      }
    } else if ("fromColor" in effectiveColor) {
      from = effectiveColor.fromColor;
      to = effectiveColor.toColor;
      if (effectiveColor.useTransformArray) {
        transform = effectiveColor.useTransformArray;
      }
    } else {
      // DynamicColorConfig
      from = "#EEDFD1";
      to = "#EEDFD1";
    }

    return { fromColor: from, toColor: to, transformArray: transform };
  }, [effectiveColor]);

  // Extract dynamic color options once
  const dynamicOptions = useMemo(() => {
    if (typeof effectiveColor === "object" && "type" in effectiveColor && effectiveColor.type === "dynamic") {
      return {
        lightTextColor: effectiveColor.lightTextColor,
        darkTextColor: effectiveColor.darkTextColor,
        algorithm: effectiveColor.algorithm,
        strength: effectiveColor.strength,
      };
    }
    return undefined;
  }, [effectiveColor]);

  // For dynamic mode, create a single transform that updates with global scroll
  const dynamicColorTransform = useTransform(scrollProgress, () => {
    return getContrastingColor(undefined, dynamicOptions);
  });

  // Create a single color transform for non-dynamic modes (reused for all text elements)
  const staticColorTransform = useMemo(() => {
    if (!isDynamic && fromColor !== toColor) {
      return {
        transform: scrollYProgress,
        inputRange: transformArray,
        outputRange: [fromColor, fromColor, toColor, toColor],
        mixer: (from: string, to: string) => (p: number) => interpolateColor(from, to, p),
      };
    }
    return null;
  }, [isDynamic, fromColor, toColor, transformArray, scrollYProgress]);

  // Determine the color motion value to use
  let colorMotionValue: MotionValue<string> | string;

  if (isDynamic) {
    colorMotionValue = dynamicColorTransform;
  } else if (staticColorTransform) {
    colorMotionValue = useTransform(
      staticColorTransform.transform,
      staticColorTransform.inputRange,
      staticColorTransform.outputRange,
      { mixer: staticColorTransform.mixer }
    );
  } else {
    // Static color, no animation needed
    colorMotionValue = fromColor;
  }

  const positionClass = `heading--${position}`;
  const mobilePositionClass = mobilePosition ? `heading--mobile-${mobilePosition}` : "";
  const colorClass = typeof effectiveColor === "string" ? `heading--${effectiveColor}` : "";
  const variantClass = variant ? `heading--${variant}` : "";
  const verticalAlignClass = verticalAlign ? `heading--v-${verticalAlign}` : "";
  const textAlignClass = textAlign ? `heading--text-${textAlign}` : "";
  const mobileTextAlignClass = mobileTextAlign ? `heading--mobile-text-${mobileTextAlign}` : "";

  const containerStyle: React.CSSProperties = {
    ...(width ? { maxWidth: typeof width === "number" ? `${width}px` : width } : {}),
    ...(mobileMaxWidth ? { "--mobile-max-width": typeof mobileMaxWidth === "number" ? `${mobileMaxWidth}px` : mobileMaxWidth } as React.CSSProperties : {})
  };

  return (
    <div
      ref={headingRef}
      className={`heading ${positionClass} ${mobilePositionClass} ${colorClass} ${variantClass} ${verticalAlignClass} ${textAlignClass} ${mobileTextAlignClass} ${className}`.trim()}
      style={containerStyle}
    >
      {title && (
        <motion.h4 className={`heading-title ${multiLineTitle ? "multi-line" : ""}`} style={{ color: colorMotionValue }}>
          {title}
        </motion.h4>
      )}

      <motion.h2
        className={`heading-text ${variant ? `heading-text--${variant}` : ""}`.trim()}
        style={{ color: colorMotionValue, textWrap: wrapHeading }}
      >
        {heading}
      </motion.h2>

      {body && (
        <motion.p className="heading-body" style={{ color: colorMotionValue }}>
          {body}
        </motion.p>
      )}

      {children}
    </div>
  );
};
